'use client';

/**
 * Mounts the Web-Vitals reporter into the App Router root layout.
 *
 * Pinned by edv-6.3 (2026-05-15). Renders nothing — the only
 * side-effect is registering Next.js's `useReportWebVitals` callback
 * which fires once per metric per page view. The callback forwards
 * the metric to PostHog via `lib/telemetry/web-vitals.ts::reportWebVital`,
 * which destructures the safe-field allow-list before forwarding
 * (see that module's CJIS-payload-allow-list block).
 *
 * --------------------------------------------------------------------
 *  Singleton wiring (edv-6.3 code-review pass-1 H1 fix, 2026-05-15)
 * --------------------------------------------------------------------
 *
 * `posthog-js` v1.x npm-module consumption does NOT attach to
 * `window.posthog`. The singleton lives only in the module-scoped
 * default export. The previous attempt at this story used a
 * `globalThis.posthog` lookup that was always undefined → every
 * metric was silently dropped. Importing `posthog-js` here directly
 * binds against the same singleton that `app/providers.tsx`
 * initializes; `posthog.capture(...)` is safe to call before init
 * (the library queues / no-ops gracefully).
 *
 * ESLint `no-restricted-imports` scopes the `posthog-js` ban to
 * `components/evidence/**` (per `eslint.config.mjs`); `app/**` is
 * outside that scope, so this import is permitted.
 *
 * --------------------------------------------------------------------
 *  Mount position rationale
 * --------------------------------------------------------------------
 *
 * Mounted directly inside `<body>` in `app/layout.tsx` (sibling to
 * `<Providers>`, NOT child) so:
 *   - It captures Web Vitals for unauthenticated routes (`/login`,
 *     `/forgot-password`) just as it does for authenticated routes.
 *   - It does not re-render when `<Providers>`'s React-Query /
 *     theme contexts invalidate (the hook registration is global
 *     and wants a stable mount).
 *
 * The component returns `null` because `useReportWebVitals` is a
 * Next.js client-side hook that only sets up listeners — there is
 * nothing to render.
 */
import { useReportWebVitals } from 'next/web-vitals';
import { useCallback } from 'react';
import posthog from 'posthog-js';
import {
  reportWebVital,
  type WebVitalsMetricInput,
} from '@/lib/telemetry/web-vitals';

/**
 * Compile-time guard (edv-6.3 code-review pass-2 M3 fix, 2026-05-15):
 * lock the structural relationship between Next.js's `Metric`
 * (what `useReportWebVitals` passes to its callback) and our
 * `WebVitalsMetricInput`. Today Next 16 ships `next/web-vitals.js`
 * without a `.d.ts` for its underlying `next/dist/compiled/web-vitals`
 * sub-package, so `Metric` resolves to `any` and this assertion is
 * trivially satisfied. If a future Next patch ships proper types and
 * narrows `Metric.name` to a literal union (or drops a field we read),
 * this `Parameters<...>[0]` extraction will surface the mismatch at
 * compile time instead of at runtime against PostHog.
 *
 * The `extends` check is one-directional on purpose: we require
 * Next's metric shape to be ASSIGNABLE TO our `WebVitalsMetricInput`
 * (i.e., Next's type is a subtype). Our type carries an index
 * signature widening it; Next's metric should fit inside. If the
 * check ever evaluates to `never`, the change in Next's type surface
 * must be reconciled here AND in the `reportWebVital` destructure
 * before merging.
 */
type _NextMetricParam = Parameters<Parameters<typeof useReportWebVitals>[0]>[0];
type _AssertNextMetricCompatible = _NextMetricParam extends WebVitalsMetricInput
  ? true
  : never;
// Evaluating the type alias surfaces the mismatch at build time;
// the `export` keeps `noUnusedLocals` quiet without resorting to a
// `void` operator (SonarQube `S3735`). The constant is intentionally
// unread at runtime — it exists for the typechecker.
export const __NEXT_METRIC_COMPATIBLE__: _AssertNextMetricCompatible = true;

/**
 * Capture function bound once at module load. Hoisting out of
 * `useCallback` saves an allocation per metric emit (5-6 per
 * page-load — trivial, but pure win). The binding is safe to do
 * before `posthog.init(...)` runs: `posthog-js` queues `capture`
 * calls internally and replays them post-init (or no-ops gracefully
 * when `NEXT_PUBLIC_POSTHOG_KEY` is unset).
 */
const boundPosthogCapture = posthog.capture.bind(posthog);

export function WebVitalsReporter(): null {
  // `useCallback` keeps the handler reference stable across renders
  // so `useReportWebVitals` doesn't re-subscribe on every parent
  // re-render (Next.js's hook is stable to a stable callback).
  const onMetric = useCallback((metric: WebVitalsMetricInput) => {
    reportWebVital(metric, boundPosthogCapture);
  }, []);
  useReportWebVitals(onMetric);
  return null;
}
